import { EMPTY, expand, of, reduce, skip } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { IEpic, isTypeOfAndGetPayload } from '@cbridge-utils/redux-observable';
import { IClientApiDependencySegment, IApiSymbol, catchApiError } from '@cbridge-packages/api';
import { createApiErrorNotification } from '@cbridge-packages/notification';
import { MAX_PAGE_SIZE } from '@cbridge-utils/datagrid';
import { SymbolSyntheticAction } from '../symbolSynthetic.action';
import { SymbolSyntheticTranslator } from '../symbolSynthetic.translator';

type IDependencies = IClientApiDependencySegment;

interface ISourceSymbolsPage {
  symbols: IApiSymbol[];
  skip: number;
  isLastPage: boolean;
}

// Sources need full Core Symbol attributes (base/quote assets), so the regular
// list request is used here — unlike the excludeDetails-trimmed base symbols
const fetchSourceSymbolsPage = (pageSkip: number, dependencies: IDependencies) => {
  return dependencies.api.getSymbolList({
    limit: MAX_PAGE_SIZE,
    skip: pageSkip,
  }).pipe(
    map(({ symbols }): ISourceSymbolsPage => ({
      symbols,
      skip: pageSkip + MAX_PAGE_SIZE,
      isLastPage: symbols.length < MAX_PAGE_SIZE,
    })),
  );
};

export const loadSourceSymbolsEpic: IEpic<{}, IDependencies> = (action$, _, dependencies) => action$.pipe(
  isTypeOfAndGetPayload(SymbolSyntheticAction.loadSourceSymbols.started),
  mergeMap((params) => {
    return of<ISourceSymbolsPage>({ symbols: [], skip: 0, isLastPage: false }).pipe(
      expand((page) => (page.isLastPage ? EMPTY : fetchSourceSymbolsPage(page.skip, dependencies))),
      skip(1),
      reduce((symbols, page) => symbols.concat(page.symbols), [] as IApiSymbol[]),
      mergeMap((symbols) => {
        return [
          SymbolSyntheticAction.loadSourceSymbols.done({ params, result: symbols.map(SymbolSyntheticTranslator.toSourceSymbol) }),
        ];
      }),
      catchApiError((error) => {
        return [
          SymbolSyntheticAction.loadSourceSymbols.failed({ params, error }),
          createApiErrorNotification(error),
        ];
      }),
    );
  }),
);
